// Technician domain types — per Schema_Specification Section 3.9 (2.10.x).
// Base Technician + TechnicianListQuery live here; jobs.ts imports them for
// assignment/agenda relations. This module also owns CRUD/detail/availability.

import i18n from '@/i18n'
import type { ListQuery } from './index'

export type TechnicianStatus = 'active' | 'inactive'
export type AvailabilityType = 'available' | 'off' | 'leave'
export type TechnicianRole = 'commercial' | 'technician' | 'hybrid'

export interface TechnicianCurrentIntervention {
  job_id: string
  client_name: string | null
  city: string | null
  address: string | null
  end_time: string | null
}

export interface Technician {
  id: string
  tenant_id: string
  user_id: string | null
  full_name: string
  first_name?: string | null
  last_name?: string | null
  email: string | null
  role: TechnicianRole | null
  phone: string | null
  skills: string[] | null
  // Vehicle summaries surfaced on list/export responses when available.
  vehicles?: TechnicianVehicleItem[] | null
  // Intervention zones surfaced on the list (feedback #72), each shown as a badge.
  zones?: string[] | null
  // Resolved avatar photo URL (feedback #79); falls back to initials when null.
  avatar_url?: string | null
  country?: string | null
  postal_code?: string | null
  address?: string | null
  latitude?: number | null
  longitude?: number | null
  city?: string | null
  certibiocide_no?: string | null
  display_status?: string | null
  current_intervention?: TechnicianCurrentIntervention | null
  on_leave?: boolean
  leave_from?: string | null
  leave_to?: string | null
  jobs_completed?: number
  revenue_generated?: number
  punctuality_rate?: number | null
  success_rate?: number | null
  status: 'active' | 'inactive'
  working_hours: Record<string, unknown> | null
  // Optional client-side derived display color (per-technician agenda color).
  color?: string | null
  created_at: string
  updated_at: string
}

export interface TechnicianListQuery extends ListQuery {
  // Multi-select on the API side (2.10.1): active | inactive | on_leave |
  // on_intervention, matched against the technician's DISPLAYED status.
  status?: string | string[]
  skills?: string[]
  city?: string
  top_revenue_first?: boolean
}

export interface TechnicianCertification {
  name: string
  valid_until?: string | null
}

export interface TechnicianRevenueByActivityItem {
  activity: string
  amount: number
}

export interface TechnicianDetail extends Technician {
  phone_secondary?: string | null
  avatar_file_id?: string | null
  avatar_url?: string | null
  certifications?: TechnicianCertification[] | null
  country?: string | null
  city?: string | null
  postal_code?: string | null
  address?: string | null
  certibiocide_no?: string | null
  latitude?: number | null
  longitude?: number | null
  intervention_radius_km?: number | null
  commission_rates?: Record<string, number>
  equipment?: TechnicianEquipmentItem[]
  vehicles?: TechnicianVehicleItem[]
  works_weekends?: boolean
  on_leave?: boolean
  leave_from?: string | null
  leave_to?: string | null
  block_in_schedule?: boolean
  display_status?: string
  current_intervention?: TechnicianCurrentIntervention | null
  notes?: string | null
  welcome_email_sent_at?: string | null
  welcome_email_failed_at?: string | null
  welcome_email_pending?: boolean
  jobs_completed?: number
  revenue_generated?: number
  punctuality_rate?: number | null
  success_rate?: number | null
  documents_count: number
  completed_jobs_count: number // derived (all time)
  active_jobs_count: number // derived
  revenue_by_activity?: TechnicianRevenueByActivityItem[]
}

export interface TechnicianAvailability {
  id: string
  tenant_id: string
  technician_id: string
  date: string
  start_time: string | null
  end_time: string | null
  availability_type: AvailabilityType
}

export interface TechnicianWorkingHours {
  days: string[]
  start: string
  end: string
  // Daily break window as a "HH:MM-HH:MM" range (feedback #74).
  break?: string | null
}

export interface TechnicianEquipmentItem {
  name: string
  operational?: boolean
}

export interface TechnicianVehicleItem {
  name: string
  plate?: string
}

export interface CreateTechnicianRequest {
  first_name: string
  last_name: string
  status?: TechnicianStatus
  email?: string
  role: TechnicianRole
  phone?: string
  phone_secondary?: string
  country?: string
  city?: string
  postal_code?: string
  address?: string
  certibiocide_no?: string
  latitude?: number
  longitude?: number
  // null clears the avatar on the API side; omit the key to keep the current one.
  avatar_file_id?: string | null
  skills?: string[]
  // Intervention zones (feedback #72 / #77), persisted as a json string array.
  zones?: string[]
  // Certifications (feedback #78): name + optional expiry date.
  certifications?: TechnicianCertification[]
  commission_rates?: Record<string, number>
  equipment?: TechnicianEquipmentItem[]
  vehicles?: TechnicianVehicleItem[]
  works_weekends?: boolean
  working_hours?: TechnicianWorkingHours
  on_leave?: boolean
  leave_from?: string
  leave_to?: string
  intervention_radius_km?: number
  block_in_schedule?: boolean
  notes?: string
}

export type EditTechnicianRequest = Partial<CreateTechnicianRequest>

export interface AvailabilityRequest {
  date: string
  start_time?: string
  end_time?: string
  availability_type?: AvailabilityType
}

// Status labels for the operator portal (2.10.1-027). "On Intervention" is a
// dynamic derived state layered on top of the persisted active|inactive value.
export const TECHNICIAN_STATUS_LABELS: Record<string, string> = {
  active: i18n.t('enums:technicianStatus.active'),
  inactive: i18n.t('enums:technicianStatus.inactive'),
  on_leave: i18n.t('enums:technicianStatus.on_leave'),
  on_intervention: i18n.t('enums:technicianStatus.on_intervention'),
}
